import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import formatUsername from "discourse/helpers/format-username";
import routeAction from "discourse/helpers/route-action";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { userPath } from "discourse/lib/url";
import dFormatDate from "discourse/ui-kit/helpers/d-format-date";
import { i18n } from "discourse-i18n";
import PostVotingButton from "./post-voting-button";
import PostVotingCommentActions from "./post-voting-comment-actions";
import PostVotingCommentEditor from "./post-voting-comment-editor";

export function buildAnchorId(commentId) {
  return `post-voting-comment-${commentId}`;
}

export default class PostVotingComment extends Component {
  @service currentUser;

  @tracked isEditing = false;
  @tracked isVoting = false;

  get anchorId() {
    return buildAnchorId(this.args.comment.id);
  }

  @action
  onSave(comment) {
    this.args.updateComment(comment);
    this.collapseEditor();
  }

  @action
  onCancel() {
    this.collapseEditor();
  }

  @action
  removeVote() {
    this.isVoting = true;

    this.args.removeVote(this.args.comment.id);

    return ajax("/post_voting/vote/comment", {
      type: "DELETE",
      data: { comment_id: this.args.comment.id },
    })
      .catch((e) => {
        this.args.vote(this.args.comment.id);
        popupAjaxError(e);
      })
      .finally(() => {
        this.isVoting = false;
      });
  }

  @action
  vote(direction) {
    if (direction !== "up") {
      return;
    }

    this.isVoting = true;

    this.args.vote(this.args.comment.id);

    return ajax("/post_voting/vote/comment", {
      type: "POST",
      data: { comment_id: this.args.comment.id },
    })
      .catch((e) => {
        this.args.removeVote(this.args.comment.id);
        popupAjaxError(e);
      })
      .finally(() => {
        this.isVoting = false;
      });
  }

  @action
  expandEditor() {
    this.isEditing = true;
  }

  @action
  collapseEditor() {
    this.isEditing = false;
  }

  <template>
    <div
      class="post-voting-comments__comment {{if @comment.deleted '--deleted'}}"
      id={{this.anchorId}}
    >
      {{#if this.isEditing}}
        <PostVotingCommentEditor
          @id={{@comment.id}}
          @onCancel={{this.onCancel}}
          @onSave={{this.onSave}}
          @raw={{@comment.raw}}
        />
      {{else}}
        <div class="post-voting-comments__vote">
          {{#if @comment.post_voting_vote_count}}
            <span
              class="post-voting-comments__vote-count"
            >{{@comment.post_voting_vote_count}}</span>
          {{else}}
            <span class="post-voting-comments__vote-count --none">0</span>
          {{/if}}

          <PostVotingButton
            @direction="up"
            @disabled={{@disabled}}
            @loading={{@isVoting}}
            @removeVote={{this.removeVote}}
            @vote={{if this.currentUser this.vote (routeAction "showLogin")}}
            @voted={{@comment.user_voted}}
          />
        </div>

        <div class="post-voting-comments__comment-post">
          <span class="post-voting-comments__comment-cooked">{{trustHTML
              @comment.cooked
            }}</span>

          <span class="post-voting-comments__separator">–</span>

          {{#if @comment.username}}
            <a
              class="post-voting-comments__username"
              data-user-card={{@comment.username}}
              href={{userPath @comment.username}}
            >
              {{formatUsername @comment.username}}
            </a>
          {{else}}
            <span class="post-voting-comments__username --deleted">
              {{i18n "post_voting.post.post_voting_comment.user.deleted"}}
            </span>
          {{/if}}

          <span class="post-voting-comments__created">
            {{dFormatDate @comment.created_at}}
          </span>

          <PostVotingCommentActions
            @comment={{@comment}}
            @disabled={{@disabled}}
            @id={{@comment.id}}
            @removeComment={{@removeComment}}
            @updateComment={{this.expandEditor}}
          />

        </div>
      {{/if}}
    </div>
  </template>
}
